import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ExternalLink,
  Film,
  ShieldCheck,
  MessageCircle,
  Save,
  Sparkles,
} from "lucide-react";
import { useAbarytDate } from "../context/AbarytDateContext";
import ScriptureHubNav from "../components/ScriptureHubNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YAH_SCRIPTURES_URL = "https://yahscriptures.online/";

export default function DailyDevotional() {
  const { dayOfYear: routeDay } = useParams();
  const navigate = useNavigate();
  const { date, fallbackDate } = useAbarytDate();
  const effectiveDate = date || fallbackDate;
  const currentDayOfYear = effectiveDate?.dayOfYear || 1;
  const selectedDay = Math.max(1, Math.min(364, Number(routeDay || currentDayOfYear)));

  const [entry, setEntry] = useState(null);
  const [archive, setArchive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [savedItems, setSavedItems] = useState([]);

  useEffect(() => {
    let active=true;
    setLoading(true);
    setEntry(null);
    // The public reading must not wait for account, archive or saved-item requests.
    axios.get(`${API}/daily-devotional/day/${selectedDay}`)
      .then(response=>{if(active)setEntry(response.data||null)})
      .catch(()=>{if(active)setEntry(null)})
      .finally(()=>{if(active)setLoading(false)});
    return ()=>{active=false};
  },[selectedDay]);

  useEffect(()=>{
    let active=true;
    axios.get(`${API}/daily-devotional/archive`,{params:{through_day_of_year:currentDayOfYear,limit:45}})
      .then(response=>{if(active)setArchive(response.data||[])})
      .catch(()=>{if(active)setArchive([])});
    return ()=>{active=false};
  },[currentDayOfYear]);

  useEffect(()=>{
    let active=true;
    Promise.all([
      axios.get(`${API}/nationals/me`,{withCredentials:true}).catch(()=>({data:null})),
      axios.get(`${API}/daily-devotional/saves`,{withCredentials:true}).catch(()=>({data:[]}))
    ]).then(([u,saves])=>{
      if(!active)return;
      setUser(u.data||null);
      setSavedItems((saves.data||[]).find(item=>Number(item.day_of_year)===selectedDay)?.items||[]);
    });
    return ()=>{active=false};
  },[selectedDay]);

  const canGoNext = selectedDay < currentDayOfYear;
  const isSignedIn = Boolean(user);

  const toggleSave = async (item) => {
    if (!isSignedIn) {
      navigate("/login?next=" + encodeURIComponent("/daily-devotional/" + selectedDay));
      return;
    }
    const next = savedItems.includes(item)
      ? savedItems.filter((value) => value !== item)
      : [...savedItems, item];
    setSavedItems(next);
    try {
      await axios.put(
        `${API}/daily-devotional/saves/${selectedDay}`,
        { items: next },
        { withCredentials: true }
      );
    } catch {
      setSavedItems(savedItems);
    }
  };

  const blocks = useMemo(() => {
    if (!entry) return [];
    return [
      {
        key: "scripture",
        label: "Scripture",
        icon: BookOpen,
        content: (
          <>
            {entry.text ? (
              <div className="font-heading text-2xl sm:text-3xl leading-relaxed text-slate-800 whitespace-pre-line">
                {entry.text_status === "verified_source" || entry.text_status === "manual_override"
                  ? <>“{entry.text}”</>
                  : entry.text}
                {entry.text_status === "study_excerpt" && (
                  <p className="mt-2 font-sans text-sm text-slate-600">
                    Study excerpt — not verified word-for-word against YAH Scriptures.
                  </p>
                )}
              </div>
            ) : (
              <div className="border-l-4 border-kingdom-gold pl-4">
                <p className="font-heading text-2xl sm:text-3xl leading-relaxed text-slate-800">
                  {entry.reference}
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  Open the source below for the verified YAH Scriptures wording.
                </p>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="font-bold text-royal-blue">{entry.reference}</span>
              <span className="text-sm font-semibold text-slate-500">YAH Scriptures</span>
              <a
                href={entry.source_url || YAH_SCRIPTURES_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-royal-blue underline underline-offset-4"
              >
                Open YAH Scriptures <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </>
        ),
      },
      {
        key: "video",
        label: "Watch",
        icon: Film,
        content: entry.video_url ? (
          <div>
            {entry.video_title && <h3 className="font-heading text-xl text-royal-blue mb-3">{entry.video_title}</h3>}
            <video
              className="w-full rounded-sm bg-black"
              controls
              playsInline
              poster={entry.video_poster_url || undefined}
              src={entry.video_url}
            />
          </div>
        ) : (
          <p className="text-slate-500">No video has been published for this devotional yet.</p>
        ),
      },
      {
        key: "devotional",
        label: "Reflection",
        icon: Sparkles,
        content: (
          <>
            <h3 className="font-heading text-2xl text-royal-blue">{entry.devotional_title || entry.theme}</h3>
            <p className="mt-4 whitespace-pre-line text-slate-700 leading-8">{entry.devotional_body}</p>
          </>
        ),
      },
      {
        key: "encouragement",
        label: "Be encouraged",
        icon: ShieldCheck,
        content: <p className="font-heading text-2xl sm:text-3xl leading-relaxed text-slate-800">{entry.encouragement}</p>,
      },
      {
        key: "palal",
        label: "Palal",
        icon: MessageCircle,
        content: <p className="font-heading text-xl sm:text-2xl leading-relaxed text-slate-800">{entry.palal}</p>,
      },
    ];
  }, [entry]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-parchment text-slate-500">Loading devotional…</div>;
  }

  if (!entry) {
    return <div className="min-h-[60vh] flex items-center justify-center bg-parchment text-slate-500">Devotional unavailable.</div>;
  }

  return (
    <div className="bg-parchment min-h-screen" data-testid="daily-devotional-page">
      <ScriptureHubNav currentDayOfYear={currentDayOfYear} />
      <section className="bg-royal-blue text-white border-b-4 border-kingdom-gold">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 hover:text-kingdom-gold">
              <ArrowLeft className="w-4 h-4" /> gov.yasharal.vip
            </Link>
            <div className="text-sm text-white/70">
              Month {entry.month} · Day {entry.day} · Year 5926
            </div>
          </div>

          <div className="mt-7 max-w-3xl">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-kingdom-gold">
              Daily Word & Devotional
            </p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl">
              {entry.devotional_title || entry.theme}
            </h1>
            <p className="mt-4 text-white/80">
              {entry.is_shabat ? "Shabat reading" : entry.appointed_time || entry.theme}
            </p>
          </div>

          <div className="mt-7 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(`/daily-devotional/${Math.max(1, selectedDay - 1)}`)}
              disabled={selectedDay <= 1}
              className="inline-flex items-center gap-2 border border-white/40 px-4 py-2 text-sm font-semibold disabled:opacity-40"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>
            <button
              type="button"
              onClick={() => navigate(`/daily-devotional/${selectedDay + 1}`)}
              disabled={!canGoNext}
              className="inline-flex items-center gap-2 border border-white/40 px-4 py-2 text-sm font-semibold disabled:opacity-40"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
            {selectedDay !== currentDayOfYear && (
              <button
                type="button"
                onClick={() => navigate(`/daily-devotional/${currentDayOfYear}`)}
                className="bg-kingdom-gold px-4 py-2 text-sm font-bold text-royal-blue"
              >
                Today
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-[1fr_300px] gap-8">
        <main className="space-y-5">
          {blocks.map(({ key, label, icon: Icon, content }) => (
            <section key={key} className="bg-white border border-kingdom-gold/30 p-5 sm:p-7 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-royal-blue">
                  <Icon className="w-5 h-5 text-kingdom-gold" />
                  <h2 className="text-xs font-black uppercase tracking-[0.18em]">{label}</h2>
                </div>

                {isSignedIn && (
                  <button
                    type="button"
                    onClick={() => toggleSave(key)}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold ${savedItems.includes(key) ? "text-kingdom-gold" : "text-slate-500"}`}
                  >
                    <Save className="w-4 h-4" />
                    {savedItems.includes(key) ? "Saved" : "Save"}
                  </button>
                )}
              </div>
              <div className="mt-5">{content}</div>
            </section>
          ))}

          {!isSignedIn && (
            <section className="bg-royal-blue text-white p-5 sm:p-6">
              <h2 className="font-heading text-2xl">Want to save this study?</h2>
              <p className="mt-2 text-white/80">
                Reading is public. Any signed-in account can save the Scripture, video, reflection, encouragement, or palal to My Library.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={"/login?next=" + encodeURIComponent("/daily-devotional/" + selectedDay)}
                  className="bg-kingdom-gold text-royal-blue px-4 py-2 font-bold"
                >
                  Sign in
                </Link>
                <Link to="/my-library" className="border border-white/50 px-4 py-2 font-semibold">My Library</Link>
              </div>
            </section>
          )}
        </main>

        <aside>
          <div className="sticky top-28 bg-white border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-heading text-xl text-royal-blue">Past devotionals</h2>
              <p className="mt-1 text-xs text-slate-500">See where we have been.</p>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100">
              {archive.map((item) => (
                <Link
                  key={item.day_of_year}
                  to={`/daily-devotional/${item.day_of_year}`}
                  className={`block p-4 hover:bg-slate-50 ${item.day_of_year === selectedDay ? "bg-kingdom-gold/10" : ""}`}
                >
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    M{item.month} · D{item.day} {item.is_shabat ? "· Shabat" : ""}
                  </div>
                  <div className="mt-1 font-semibold text-royal-blue">{item.reference}</div>
                  <div className="mt-1 text-xs text-slate-600 line-clamp-2">{item.devotional_title || item.theme}</div>
                  {item.has_video && <div className="mt-2 text-[11px] font-bold text-kingdom-gold">VIDEO</div>}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
